import React, { useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Checkbox } from '../../Checkbox';
import CollapsableList from '../CollapsableList/CollapsableList';
import HelpText from '../CommonComponents/HelpText/HelpText';
import { DropdownContext } from '../context';
import { OptionTypes, searchedText, searchMetaData, DropdownTypes, generateItemKey, removePlaceholder, defaultSelectedValue, filterDisabledItems, filterOutItems } from '../helper';
import styles from '@dtsl/css-dropdown/dist/index.module.css';
function MultiSelectionList(props) {
  let {
    options,
    onSelect,
    isDropdownButton,
    noOptionContent,
    placeHolder,
    dropdownType,
    selectAllIdentifier = ''
  } = props;
  const {
    selectedValues,
    setSelectedValues,
    searchValue
  } = useContext(DropdownContext);
  options = searchedText(searchValue, [...options]);
  const setValue = (selectedObj, event) => {
    const {
      label,
      key
    } = selectedObj;
    const hasValue = selectedValues.some(item => item.label === label && item.key === key);
    let filteredValues = [];
    if (hasValue) {
      filteredValues = selectedValues.filter(item => !(item.label === label && item.key === key));
    } else {
      filteredValues = [...selectedValues, selectedObj];
    }
    !isDropdownButton && setSelectedValues(filteredValues);
    fireChangeEvent(filteredValues, event);
  };
  const selectAll = (isChecked, filteredValues, event) => {
    event.stopPropagation();
    if (isChecked) {
      fireChangeEvent([...filteredValues], event);
    } else {
      const selectedAllValues = options.reduce((result, current, currentIndex) => {
        const {
          type
        } = current || {};
        if (type === OptionTypes.COLLAPSABLE_LIST) {
          const {
            items
          } = current;
          const newItems = items.map((item, index) => {
            return {
              ...item,
              key: item?.key || generateItemKey(currentIndex, index)
            };
          });
          return result.concat(filterDisabledItems(newItems));
        } else {
          if (!current.disabled) {
            return result.concat({
              ...current,
              key: current.key || currentIndex
            });
          }
          return result;
        }
      }, []);
      fireChangeEvent([defaultSelectedValue(placeHolder), ...selectedAllValues], event);
    }
  };
  const fireChangeEvent = (filteredValues, event) => {
    if (onSelect) {
      onSelect(filteredValues, event);
    }
  };
  const renderOption = (option, index) => {
    let {
      key = index,
      label,
      addtionalInfo = () => {},
      highLightedLabel,
      helpText,
      disabled,
      checkboxIdentifier = ''
    } = option;
    const isChecked = selectedValues.some(selectedValue => {
      return selectedValue.label === label && selectedValue.key === key;
    });
    const optionClass = classNames({
      [styles['sib-dropdown-multi-option']]: true,
      [option.className]: !!option.className,
      [styles['locked']]: disabled
    });
    const primaryInfoClass = classNames({
      [styles['primary-info']]: true,
      [styles['sib-remove-bottomspace']]: !helpText
    });
    const addInfo = addtionalInfo();
    return /*#__PURE__*/React.createElement("li", {
      key: `${key}_${label}_${index}`,
      className: optionClass,
      role: "option",
      "aria-selected": isChecked ? 'true' : 'false',
      onClick: disabled ? () => {} : () => setValue({
        ...option,
        key
      }, event),
      onKeyDown: event => {
        if (event.key === 'Enter' && !disabled) {
          setValue({
            ...option,
            key
          }, event);
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-dropdown-multi-option__content'],
      title: label,
      disabled: disabled
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-dropdown-multi-option__content-left']
    }, /*#__PURE__*/React.createElement("span", {
      className: primaryInfoClass
    }, /*#__PURE__*/React.createElement(Checkbox, {
      ellipsis: true,
      disabled: disabled,
      label: searchValue ? /*#__PURE__*/React.createElement("span", {
        dangerouslySetInnerHTML: {
          __html: highLightedLabel
        }
      }) : label,
      checked: isChecked,
      identifier: checkboxIdentifier ? checkboxIdentifier : `sibCheckbox_${key}`,
      className: styles['sib-checkbox-wrapper'],
      labelClassName: styles['sib-checkbox-label'],
      labelContainerClassName: `${styles['sib-checkbox-label']} ${styles['sib-checkbox-label-alias']}`,
      variant: "positive",
      onChange: event => {
        event.stopPropagation();
      },
      onClick: e => {
        e.stopPropagation();
      },
      onKeyDown: event => {
        if (event.which === 13 || event.which === 32) {
          event.stopPropagation();
          setValue({
            ...option,
            key
          }, event);
        }
      }
    })), helpText && /*#__PURE__*/React.createElement(HelpText, {
      helpText: helpText
    })), addInfo ? /*#__PURE__*/React.createElement("span", {
      className: styles['additional-info']
    }, addInfo) : null));
  };
  const renderSelectAll = () => {
    const totalSelectedValues = removePlaceholder(selectedValues, placeHolder).length;
    let optionLength = 0;
    let totalDisabledItems = 0;
    options.forEach(option => {
      const {
        type,
        disabled
      } = option || {};
      if (type === OptionTypes.COLLAPSABLE_LIST) {
        const {
          items
        } = option;
        if (Array.isArray(items)) {
          const totalSelectableItems = filterDisabledItems(items).length;
          optionLength = optionLength + totalSelectableItems;
          totalDisabledItems = totalDisabledItems + items.length - totalSelectableItems;
        }
      } else {
        disabled ? totalDisabledItems++ : optionLength++;
      }
    });
    const isChecked = totalSelectedValues > 0;
    const variant = totalSelectedValues === optionLength ? 'positive' : 'undetermined';
    const searchAllLabel = `${searchMetaData.getSelectAllText} (${totalSelectedValues}/${optionLength})`;
    return searchMetaData?.getSelectAllText ? /*#__PURE__*/React.createElement("li", {
      className: styles['sib-dropdown-multi-option'],
      role: "option",
      "aria-selected": isChecked ? 'true' : 'false'
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-dropdown-multi-option__content'],
      title: searchAllLabel,
      onClick: event => selectAll(isChecked, [defaultSelectedValue(placeHolder)], event),
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: `${styles['primary-info']} ${styles['select-all']}`
    }, /*#__PURE__*/React.createElement(Checkbox, {
      ellipsis: true,
      label: searchAllLabel,
      identifier: selectAllIdentifier ? selectAllIdentifier : 'sibCheckbox_selectAll',
      checked: isChecked,
      labelClassName: styles['sib-checkbox-label'],
      labelContainerClassName: `${styles['sib-checkbox-label']} ${styles['sib-checkbox-label-alias']}`,
      className: styles['sib-checkbox-wrapper'],
      variant: variant,
      onChange: event => {
        event.stopPropagation();
      },
      onClick: e => {
        e.stopPropagation();
      },
      onKeyDown: event => {
        if (event.which === 13 || event.which === 32) {
          event.stopPropagation();
          selectAll(isChecked, [defaultSelectedValue(placeHolder)], event);
        }
      }
    })))) : null;
  };
  const renderSelectAllResults = () => {
    const selectedValueseExcludingOptions = filterOutItems(selectedValues, options);
    const searchedOptionLength = selectedValues.length - selectedValueseExcludingOptions.length;
    const isChecked = searchedOptionLength > 0;
    const variant = searchedOptionLength === options.length ? 'positive' : 'undetermined';
    const searchAllLabel = `${searchMetaData.getSelectAllResultsText} (${searchedOptionLength}/${options.length})`;
    return searchMetaData?.getSelectAllText ? /*#__PURE__*/React.createElement("li", {
      className: styles['sib-dropdown-multi-option'],
      role: "option",
      "aria-selected": isChecked ? 'true' : 'false'
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-dropdown-multi-option__content'],
      title: searchAllLabel,
      onClick: event => {
        event.stopPropagation();
        selectAll(isChecked, selectedValueseExcludingOptions, event);
      },
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: `${styles['primary-info']} ${styles['select-all']}`
    }, /*#__PURE__*/React.createElement(Checkbox, {
      ellipsis: true,
      label: searchAllLabel,
      checked: isChecked,
      variant: variant,
      labelClassName: styles['sib-checkbox-label'],
      labelContainerClassName: `${styles['sib-checkbox-label']} ${styles['sib-checkbox-label-alias']}`,
      className: styles['sib-checkbox-wrapper'],
      onChange: event => {
        event.stopPropagation();
      },
      onClick: e => {
        e.stopPropagation();
      },
      onKeyDown: event => {
        if (event.which === 13 || event.which === 32) {
          event.stopPropagation();
          selectAll(isChecked, selectedValueseExcludingOptions, event);
        }
      }
    })))) : null;
  };
  let ops = options.map((option, index) => {
    const {
      type
    } = option || {};
    if (type === OptionTypes.COLLAPSABLE_LIST) {
      let _options = option.items.map((item, idx) => renderOption(item, item?.key || generateItemKey(index, idx)));
      return /*#__PURE__*/React.createElement(CollapsableList, {
        key: option.name,
        heading: option.name,
        dropdownType: dropdownType,
        items: option.items || [],
        index: index,
        fireChangeEvent: fireChangeEvent,
        defaultOpen: true
      }, _options);
    } else {
      return renderOption(option, index);
    }
  });
  return ops.length ? /*#__PURE__*/React.createElement(React.Fragment, null, searchValue ? renderSelectAllResults() : renderSelectAll(), ops) : /*#__PURE__*/React.createElement("div", {
    className: `${styles['sib-dropdown-option']} ${styles['sib-dropdown-no_option']}`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-dropdown-option__content']
  }, noOptionContent));
}
MultiSelectionList.defaultProps = {
  options: [],
  isLoading: false,
  onSelect: () => {},
  isDropdownButton: false,
  noOptionContent: 'No options to select',
  dropdownType: DropdownTypes.MULTI_SELECTION,
  selectAllIdentifier: ''
};
MultiSelectionList.propTypes = {
  isLoading: PropTypes.bool,
  placeHolder: PropTypes.string,
  onSelect: PropTypes.func,
  isDropdownButton: PropTypes.bool,
  noOptionContent: PropTypes.string,
  dropdownType: PropTypes.string,
  selectAllIdentifier: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    value: PropTypes.any,
    svg: PropTypes.node,
    addtionalInfo: PropTypes.func,
    helpText: PropTypes.string
  }))
};
export default MultiSelectionList;